import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { formatCurrency, formatDate } from "../lib/utils-image";
import { isMobileDevice, canShareFile, shareFile, downloadBlob } from "../lib/file-delivery";
import { categoryBadgeStyle, categoryLabel } from "../lib/categories";
import { useCategories } from "../lib/useCategories";
import { toast } from "sonner";
import {
  Search,
  Filter,
  Eye,
  Pen,
  Trash2,
  Download,
  X,
  Calendar,
  Plus,
} from "lucide-react";

const months = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

export default function InvoiceRegisterPage() {
  const { categories } = useCategories();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("SAR");
  const [filters, setFilters] = useState({
    month: "",
    year: "",
    date_from: "",
    date_to: "",
    store_name: "",
    min_amount: "",
    max_amount: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editing, setEditing] = useState(null);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => now - i);
  }, []);

  const load = async () => {
    setLoading(true);
    const params = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
    );
    try {
      const [r, s] = await Promise.all([
        api.get("/invoices", { params: { ...params, light: true } }),
        api.get("/settings"),
      ]);
      setInvoices(r.data || []);
      setCurrency(s.data?.currency || "SAR");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // eslint-disable-next-line no-console
      console.error("Failed to load invoices:", { status, detail, err });
      if (status === 401) {
        toast.error("انتهت الجلسة، يرجى تسجيل الدخول مرة أخرى");
      } else if (status === 403 && detail) {
        toast.error(detail);
      } else if (!err?.response) {
        toast.error("تعذر الاتصال بالخادم — تحقق من الإنترنت");
      } else {
        toast.error(detail || `تعذر تحميل الفواتير (خطأ ${status || "غير معروف"})`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const applyFilters = () => {
    load();
    setShowFilters(false);
  };

  const resetFilters = () => {
    setFilters({
      month: "", year: "", date_from: "", date_to: "",
      store_name: "", category: "", min_amount: "", max_amount: "",
    });
    setTimeout(load, 0);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success("تم حذف الفاتورة");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("تعذر الحذف");
    }
  };

  const downloadImage = async (inv) => {
    // Fetch full invoice (with image) on-demand — list is light-mode without images.
    if (!inv.has_image && !inv.image_base64) {
      toast.error("لا توجد صورة لهذه الفاتورة");
      return;
    }
    let imageData = inv.image_base64;
    if (!imageData) {
      try {
        const res = await api.get(`/invoices/${inv.id}`);
        imageData = res.data?.image_base64;
        if (!imageData) {
          toast.error("لا توجد صورة لهذه الفاتورة");
          return;
        }
      } catch (e) {
        toast.error("تعذر تحميل الصورة");
        return;
      }
    }
    const src = imageData.startsWith("data:") ? imageData : `data:image/jpeg;base64,${imageData}`;
    const filename = `invoice-${inv.invoice_number || inv.id.slice(0, 8)}.jpg`;
    const blob = await (await fetch(src)).blob();
    if (isMobileDevice() && canShareFile(blob, filename, "image/jpeg")) {
      try {
        await shareFile(blob, filename, "image/jpeg");
        return;
      } catch { /* fall through to download */ }
    }
    downloadBlob(blob, filename);
  };

  const activeFiltersCount = Object.values(filters).filter((v) => v !== "").length;

  return (
    <div className="space-y-6 fade-up" data-testid="invoice-register-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">سجل الفواتير</div>
          <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">
            جميع فواتيرك
          </h1>
          <p className="text-[#5C605A] mt-2">{invoices.length} فاتورة</p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <button
            onClick={() => setShowFilters((s) => !s)}
            data-testid="toggle-filters-btn"
            className="bg-white border border-[#1A1C19]/20 hover:border-[#1A1C19] transition-colors rounded-md px-4 py-2.5 font-medium font-readex inline-flex items-center gap-2"
          >
            <Filter className="w-4 h-4" strokeWidth={1.5} />
            فلترة
            {activeFiltersCount > 0 && (
              <span className="bg-[#1F3A33] text-white text-xs rounded-full w-5 h-5 inline-flex items-center justify-center font-mono-num">
                {activeFiltersCount}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate("/add")}
            data-testid="register-add-btn"
            className="bg-[#1F3A33] text-white hover:bg-[#172A25] transition-colors rounded-md px-4 py-2.5 font-medium font-readex inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            إضافة
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white border border-[#1A1C19]/10 rounded-md p-5 sm:p-6 fade-up" data-testid="filters-panel">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FilterField label="الشهر">
              <select value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} className="input" data-testid="filter-month">
                <option value="">جميع الأشهر</option>
                {months.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="السنة">
              <select value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })} className="input" data-testid="filter-year">
                <option value="">جميع السنوات</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </FilterField>
            <FilterField label="من تاريخ">
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="input" data-testid="filter-date-from" />
            </FilterField>
            <FilterField label="إلى تاريخ">
              <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="input" data-testid="filter-date-to" />
            </FilterField>
            <FilterField label="اسم المتجر">
              <input value={filters.store_name} onChange={(e) => setFilters({ ...filters, store_name: e.target.value })} className="input" placeholder="ابحث…" data-testid="filter-store" />
            </FilterField>
            <FilterField label="الصنف">
              <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="input" data-testid="filter-category">
                <option value="">جميع الأصناف</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FilterField>
            <FilterField label="أقل مبلغ">
              <input type="number" step="0.01" value={filters.min_amount} onChange={(e) => setFilters({ ...filters, min_amount: e.target.value })} className="input font-mono-num" data-testid="filter-min" />
            </FilterField>
            <FilterField label="أعلى مبلغ">
              <input type="number" step="0.01" value={filters.max_amount} onChange={(e) => setFilters({ ...filters, max_amount: e.target.value })} className="input font-mono-num" data-testid="filter-max" />
            </FilterField>
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={applyFilters} className="bg-[#1F3A33] text-white hover:bg-[#172A25] rounded-md px-5 py-2.5 font-medium font-readex" data-testid="apply-filters-btn">تطبيق</button>
            <button onClick={resetFilters} className="text-[#5C605A] hover:text-[#1A1C19] rounded-md px-5 py-2.5 font-medium font-readex" data-testid="reset-filters-btn">إعادة تعيين</button>
          </div>
        </div>
      )}

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-32 shimmer rounded-md" />)
        ) : invoices.length === 0 ? (
          <EmptyState onAdd={() => navigate("/add")} />
        ) : (
          invoices.map((inv, idx) => (
            <div key={inv.id} className="bg-white border border-[#1A1C19]/10 rounded-md p-4 fade-up shadow-3d-sm hover:shadow-3d transition-all duration-300 hover:-translate-y-[2px]" data-testid={`invoice-card-${idx}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-readex font-medium text-[#1A1C19] truncate">{inv.store_name || "—"}</div>
                  <div className="text-xs text-[#8A8D86] mt-1">
                    {formatDate(inv.invoice_date)} · {inv.invoice_time || "—"}
                  </div>
                  <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-readex" style={categoryBadgeStyle(inv.category)} data-testid={`card-category-${idx}`}>
                    {categoryLabel(inv.category)}
                  </span>
                  {inv.invoice_number && (
                    <div className="text-xs text-[#5C605A] mt-1">#{inv.invoice_number}</div>
                  )}
                </div>
                <div className="text-end shrink-0">
                  <div className="font-mono-num font-medium text-[#C84C31]">
                    − {formatCurrency(inv.total_amount, currency)}
                  </div>
                  <div className="text-xs text-[#8A8D86] mt-1 font-mono-num">
                    رصيد: {formatCurrency(inv.balance_after, currency)}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 mt-3 pt-3 border-t border-[#1A1C19]/5">
                <IconBtn onClick={() => setViewing(inv)} title="عرض" testid={`view-${idx}`}><Eye className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => setEditing(inv)} title="تعديل" testid={`edit-${idx}`}><Pen className="w-4 h-4" /></IconBtn>
                {inv.has_image && <IconBtn onClick={() => downloadImage(inv)} title="تحميل الصورة" testid={`download-${idx}`}><Download className="w-4 h-4" /></IconBtn>}
                <IconBtn onClick={() => setDeleteTarget(inv)} title="حذف" danger testid={`delete-${idx}`}><Trash2 className="w-4 h-4" /></IconBtn>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block bg-white border border-[#1A1C19]/10 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 shimmer rounded-md" />)}
          </div>
        ) : invoices.length === 0 ? (
          <EmptyState onAdd={() => navigate("/add")} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="invoice-table">
              <thead className="bg-[#F5F4EF] border-b border-[#1A1C19]/10">
                <tr className="text-start text-xs uppercase tracking-wider text-[#5C605A] font-readex">
                  <th className="p-3 font-medium text-start">م</th>
                  <th className="p-3 font-medium text-start">التاريخ</th>
                  <th className="p-3 font-medium text-start">الوقت</th>
                  <th className="p-3 font-medium text-start">المتجر</th>
                  <th className="p-3 font-medium text-start">الصنف</th>
                  <th className="p-3 font-medium text-start">رقم الفاتورة</th>
                  <th className="p-3 font-medium text-end">المبلغ</th>
                  <th className="p-3 font-medium text-end">الضريبة</th>
                  <th className="p-3 font-medium text-end">الخصم</th>
                  <th className="p-3 font-medium text-start">الدفع</th>
                  <th className="p-3 font-medium text-end">قبل</th>
                  <th className="p-3 font-medium text-end">بعد</th>
                  <th className="p-3 font-medium text-start">الصورة</th>
                  <th className="p-3 font-medium text-start">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, idx) => (
                  <tr key={inv.id} className="border-b border-[#1A1C19]/5 hover:bg-[#F5F4EF]/50 transition-colors" data-testid={`invoice-row-${idx}`}>
                    <td className="p-3 text-sm font-mono-num text-[#8A8D86]">{idx + 1}</td>
                    <td className="p-3 text-sm font-mono-num whitespace-nowrap">{inv.invoice_date}</td>
                    <td className="p-3 text-sm font-mono-num text-[#5C605A]">{inv.invoice_time || "—"}</td>
                    <td className="p-3 text-sm font-readex font-medium">{inv.store_name || "—"}</td>
                    <td className="p-3">
                      <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-readex whitespace-nowrap" style={categoryBadgeStyle(inv.category)} data-testid={`row-category-${idx}`}>
                        {categoryLabel(inv.category)}
                      </span>
                    </td>
                    <td className="p-3 text-sm font-mono-num text-[#5C605A]">{inv.invoice_number || "—"}</td>
                    <td className="p-3 text-sm font-mono-num font-medium text-[#C84C31] text-end">{formatCurrency(inv.total_amount, currency)}</td>
                    <td className="p-3 text-sm font-mono-num text-[#5C605A] text-end">{formatCurrency(inv.tax_amount, currency)}</td>
                    <td className="p-3 text-sm font-mono-num text-[#5C605A] text-end">{formatCurrency(inv.discount_amount, currency)}</td>
                    <td className="p-3 text-sm">{inv.payment_method || "—"}</td>
                    <td className="p-3 text-sm font-mono-num text-[#5C605A] text-end">{formatCurrency(inv.balance_before, currency)}</td>
                    <td className="p-3 text-sm font-mono-num font-medium text-end">{formatCurrency(inv.balance_after, currency)}</td>
                    <td className="p-3">
                      {inv.has_image ? (
                        <button onClick={() => setViewing(inv)} className="text-[#1F3A33] underline text-sm" data-testid={`row-image-${idx}`}>عرض</button>
                      ) : (
                        <span className="text-xs text-[#8A8D86]">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <IconBtn onClick={() => setViewing(inv)} title="عرض" testid={`row-view-${idx}`}><Eye className="w-4 h-4" /></IconBtn>
                        <IconBtn onClick={() => setEditing(inv)} title="تعديل" testid={`row-edit-${idx}`}><Pen className="w-4 h-4" /></IconBtn>
                        {inv.has_image && <IconBtn onClick={() => downloadImage(inv)} title="تحميل الصورة" testid={`row-download-${idx}`}><Download className="w-4 h-4" /></IconBtn>}
                        <IconBtn onClick={() => setDeleteTarget(inv)} title="حذف" danger testid={`row-delete-${idx}`}><Trash2 className="w-4 h-4" /></IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View modal */}
      {viewing && <ViewModal invoice={viewing} currency={currency} onClose={() => setViewing(null)} />}

      {/* Delete confirm */}
      {deleteTarget && (
        <ConfirmModal
          title="تأكيد الحذف"
          message={`هل تريد حذف فاتورة ${deleteTarget.store_name || "—"} بمبلغ ${formatCurrency(deleteTarget.total_amount, currency)}؟ سيتم إعادة حساب الرصيد.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <EditModal
          invoice={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <style>{`
        .input { width:100%; background:white; border:1px solid rgba(26,28,25,0.15); border-radius:.375rem; padding:.6rem .85rem; font-size:.9rem; transition:all .2s; }
        .input:focus { outline:none; border-color:#1F3A33; box-shadow:0 0 0 3px rgba(31,58,51,0.12); }
      `}</style>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-[#5C605A] mb-1.5 font-readex">{label}</span>
      {children}
    </label>
  );
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-8 h-8 inline-flex items-center justify-center rounded-md transition-colors ${
        danger ? "text-[#C84C31] hover:bg-[#C84C31]/10" : "text-[#5C605A] hover:bg-[#F0EFEB] hover:text-[#1F3A33]"
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="p-12 text-center text-[#5C605A]">
      <Calendar className="w-10 h-10 mx-auto mb-3 text-[#8A8D86]" strokeWidth={1.5} />
      <p className="mb-4">لا توجد فواتير مطابقة. أضف أول فاتورة لتبدأ.</p>
      <button onClick={onAdd} className="bg-[#1F3A33] text-white hover:bg-[#172A25] transition-colors rounded-md px-5 py-2.5 font-medium font-readex" data-testid="empty-add-btn">
        إضافة فاتورة
      </button>
    </div>
  );
}

function ViewModal({ invoice, currency, onClose }) {
  const [fullImage, setFullImage] = useState(invoice.image_base64 || null);
  const [loadingImg, setLoadingImg] = useState(false);

  useEffect(() => {
    // Lazy-load the image if the list-mode invoice doesn't include it.
    if (invoice.has_image && !invoice.image_base64 && !fullImage) {
      setLoadingImg(true);
      api.get(`/invoices/${invoice.id}`)
        .then((r) => setFullImage(r.data?.image_base64 || null))
        .catch(() => {})
        .finally(() => setLoadingImg(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-md max-w-3xl w-full max-h-[90vh] overflow-y-auto fade-up" onClick={(e) => e.stopPropagation()} data-testid="view-modal">
        <div className="sticky top-0 bg-white border-b border-[#1A1C19]/10 px-5 py-4 flex justify-between items-center">
          <h3 className="font-readex font-semibold text-lg text-[#1A1C19]">تفاصيل الفاتورة</h3>
          <button onClick={onClose} className="text-[#5C605A] hover:text-[#1A1C19]" data-testid="view-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          {(invoice.has_image || fullImage) && (
            <div className="bg-[#F0EFEB] rounded-md p-2 flex items-center justify-center min-h-[200px]">
              {loadingImg && <span className="text-sm text-[#5C605A] font-readex">جاري تحميل الصورة…</span>}
              {fullImage && (
                <img src={fullImage.startsWith("data:") ? fullImage : `data:image/jpeg;base64,${fullImage}`} alt="" className="max-h-[500px] w-auto" />
              )}
            </div>
          )}
          <div className="space-y-3">
            <Row label="المتجر" value={invoice.store_name || "—"} />
            <Row label="التاريخ" value={invoice.invoice_date} />
            <Row label="الوقت" value={invoice.invoice_time || "—"} />
            <Row label="رقم الفاتورة" value={invoice.invoice_number || "—"} />
            <Row label="الإجمالي" value={formatCurrency(invoice.total_amount, currency)} bold />
            <Row label="الضريبة" value={formatCurrency(invoice.tax_amount, currency)} />
            <Row label="الخصم" value={formatCurrency(invoice.discount_amount, currency)} />
            <Row label="طريقة الدفع" value={invoice.payment_method || "—"} />
            <Row label="الصنف" value={categoryLabel(invoice.category)} />
            <Row label="الرصيد قبل" value={formatCurrency(invoice.balance_before, currency)} />
            <Row label="الرصيد بعد" value={formatCurrency(invoice.balance_after, currency)} bold />
            {invoice.notes && <Row label="ملاحظات" value={invoice.notes} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between py-2 border-b border-[#1A1C19]/5 last:border-0">
      <span className="text-sm text-[#5C605A]">{label}</span>
      <span className={`text-sm font-mono-num text-[#1A1C19] ${bold ? "font-semibold" : ""}`}>{value}</span>
    </div>
  );
}

function ConfirmModal({ title, message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onCancel}>
      <div className="bg-white rounded-md max-w-sm w-full p-6 fade-up" onClick={(e) => e.stopPropagation()} data-testid="confirm-modal">
        <h3 className="font-readex font-semibold text-lg text-[#1A1C19] mb-2">{title}</h3>
        <p className="text-sm text-[#5C605A] mb-5">{message}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-[#5C605A] hover:bg-[#F0EFEB] font-medium font-readex" data-testid="confirm-cancel">إلغاء</button>
          <button onClick={onConfirm} className="px-4 py-2 rounded-md bg-[#C84C31] text-white hover:bg-[#A63D26] font-medium font-readex" data-testid="confirm-delete">حذف</button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ invoice, onClose, onSaved }) {
  const { categories } = useCategories();
  const [form, setForm] = useState({
    store_name: invoice.store_name || "",
    invoice_date: invoice.invoice_date || "",
    invoice_time: invoice.invoice_time || "",
    invoice_number: invoice.invoice_number || "",
    total_amount: invoice.total_amount || 0,
    tax_amount: invoice.tax_amount || 0,
    discount_amount: invoice.discount_amount || 0,
    payment_method: invoice.payment_method || "",
    category: categoryLabel(invoice.category),
    notes: invoice.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.total_amount || Number(form.total_amount) <= 0) {
      toast.error("المبلغ يجب أن يكون أكبر من صفر");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/invoices/${invoice.id}`, {
        ...form,
        total_amount: Number(form.total_amount),
        tax_amount: Number(form.tax_amount || 0),
        discount_amount: Number(form.discount_amount || 0),
      });
      toast.success("تم التحديث");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحديث");
    } finally {
      setSaving(false);
    }
  };

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-md max-w-2xl w-full max-h-[90vh] overflow-y-auto fade-up" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
        <div className="sticky top-0 bg-white border-b border-[#1A1C19]/10 px-5 py-4 flex justify-between items-center">
          <h3 className="font-readex font-semibold text-lg text-[#1A1C19]">تعديل الفاتورة</h3>
          <button onClick={onClose} className="text-[#5C605A] hover:text-[#1A1C19]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Lbl label="المتجر"><input value={form.store_name} onChange={(e) => u("store_name", e.target.value)} className="input" data-testid="edit-store" /></Lbl>
          <div className="grid grid-cols-2 gap-4">
            <Lbl label="التاريخ"><input type="date" value={form.invoice_date} onChange={(e) => u("invoice_date", e.target.value)} className="input" data-testid="edit-date" /></Lbl>
            <Lbl label="الوقت"><input type="time" value={form.invoice_time} onChange={(e) => u("invoice_time", e.target.value)} className="input" data-testid="edit-time" /></Lbl>
          </div>
          <Lbl label="رقم الفاتورة"><input value={form.invoice_number} onChange={(e) => u("invoice_number", e.target.value)} className="input" data-testid="edit-number" /></Lbl>
          <div className="grid grid-cols-3 gap-4">
            <Lbl label="الإجمالي"><input type="number" step="0.01" value={form.total_amount} onChange={(e) => u("total_amount", e.target.value)} className="input font-mono-num" data-testid="edit-total" /></Lbl>
            <Lbl label="الضريبة"><input type="number" step="0.01" value={form.tax_amount} onChange={(e) => u("tax_amount", e.target.value)} className="input font-mono-num" data-testid="edit-tax" /></Lbl>
            <Lbl label="الخصم"><input type="number" step="0.01" value={form.discount_amount} onChange={(e) => u("discount_amount", e.target.value)} className="input font-mono-num" data-testid="edit-discount" /></Lbl>
          </div>
          <Lbl label="طريقة الدفع">
            <select value={form.payment_method} onChange={(e) => u("payment_method", e.target.value)} className="input bg-white" data-testid="edit-payment">
              <option value="">—</option>
              {["نقدي", "بطاقة", "تحويل بنكي", "محفظة إلكترونية", "أخرى"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Lbl>
          <Lbl label="الصنف">
            <select value={form.category} onChange={(e) => u("category", e.target.value)} className="input bg-white" data-testid="edit-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Lbl>
          <Lbl label="ملاحظات"><textarea value={form.notes} onChange={(e) => u("notes", e.target.value)} className="input min-h-[70px]" data-testid="edit-notes" /></Lbl>

          <div className="flex gap-2 justify-end pt-3">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-[#5C605A] hover:bg-[#F0EFEB] font-medium font-readex">إلغاء</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 rounded-md bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 font-medium font-readex" data-testid="edit-save-btn">
              {saving ? "جاري الحفظ…" : "حفظ"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Lbl({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#1A1C19] mb-1.5 font-readex">{label}</span>
      {children}
    </label>
  );
}
